import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileCheck2, FlaskConical, PackageCheck, ArrowUpRight } from 'lucide-react';
import { EASE } from './Reveal';
import { scrollToSection, lockScroll, unlockScroll } from '../../lib/scroll';
import { COMPANY } from '../../data/site';

const SPEC_KEYS = [
  ['Product Form', 'form'],
  ['Colour', 'colour'],
  ['Typical Cut / Particle Size', 'cut'],
  ['Moisture', 'moisture'],
  ['Packing Options', 'packing'],
  ['Quality', 'quality'],
];

export const ProductModal = ({ product, onClose }) => {
  const open = Boolean(product);

  useEffect(() => {
    if (!open) return;
    lockScroll();
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const handleEnquire = () => {
    window.dispatchEvent(new CustomEvent('prefill-product', { detail: product.name }));
    onClose();
    setTimeout(() => scrollToSection('#contact'), 60);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="product-modal"
          role="dialog"
          aria-modal="true"
          aria-label={product.name}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.55, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative grid max-h-[92vh] w-full max-w-4xl grid-cols-1 overflow-hidden rounded-t-3xl bg-white shadow-lift sm:rounded-3xl md:grid-cols-5"
          >
            <button
              data-testid="product-modal-close"
              onClick={onClose}
              autoFocus
              aria-label="Close product details"
              className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream/90 text-ink shadow-card transition-colors hover:bg-gold hover:text-ink"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative hidden md:col-span-2 md:block">
              <img src={product.image} alt={product.alt} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/60 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-5 right-5 font-display text-2xl font-semibold text-cream">
                {product.name}
              </p>
            </div>

            <div className="overflow-y-auto p-6 sm:p-9 md:col-span-3" data-testid="product-modal-content">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold">Product Details</p>
              <h3 className="mt-2 font-display text-2xl font-semibold text-ink md:hidden">{product.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{product.description}</p>

              <dl className="mt-6 divide-y divide-line/80">
                {SPEC_KEYS.map(([label, key]) => (
                  <div key={key} className="grid grid-cols-3 gap-4 py-3.5">
                    <dt className="text-xs font-bold uppercase tracking-wide text-forest/80">{label}</dt>
                    <dd className="col-span-2 text-sm leading-relaxed text-ink/85">{product[key]}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl bg-sage-mist p-4">
                  <FlaskConical className="h-5 w-5 text-forest" />
                  <p className="mt-2 text-xs font-bold text-ink">COA</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{product.coa}</p>
                </div>
                <div className="rounded-2xl bg-sage-mist p-4">
                  <FileCheck2 className="h-5 w-5 text-forest" />
                  <p className="mt-2 text-xs font-bold text-ink">Documentation</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{product.docs}</p>
                </div>
                <div className="rounded-2xl bg-sage-mist p-4">
                  <PackageCheck className="h-5 w-5 text-forest" />
                  <p className="mt-2 text-xs font-bold text-ink">Specification</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Final specification as per buyer PO and batch COA.
                  </p>
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  data-testid="product-modal-enquiry-btn"
                  onClick={handleEnquire}
                  className="group inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-deep hover:shadow-lift"
                >
                  Enquire About This Product
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href={COMPANY.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-modal-whatsapp-btn"
                  className="text-sm font-bold text-forest link-underline"
                >
                  Or message on WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;
